<template>
  <div class="document-upload-view">
    <div class="title-panel">
      <h2>{{ t('documentUpload.newUpload') }}</h2>
      <h1>{{ projectName }}</h1>
    </div>
    <loading-wrapper :is-loading="!contentTypeFields">
      <PVTabs v-if="contentTypeFields" v-model:value="activeTab" lazy>
        <PVTabsList>
          <router-link :to="getTabLink(DocumentUploadViewTab.MASS_UPLOAD)">
            <PVTab :value="DocumentUploadViewTab.MASS_UPLOAD">
              {{ t('documentUpload.massUpload') }}
            </PVTab>
          </router-link>
          <router-link
            v-if="!isNoMetadataUploadMode"
            :to="getTabLink(DocumentUploadViewTab.SINGLE_UPLOAD)"
          >
            <PVTab :value="DocumentUploadViewTab.SINGLE_UPLOAD">
              {{ t('documentUpload.singleUpload') }}
            </PVTab>
          </router-link>
        </PVTabsList>
        <PVTabsPanels>
          <PVTabPanel :value="DocumentUploadViewTab.MASS_UPLOAD">
            <mass-upload
              :check-bot-config-id="checkBotConfigId"
              :content-type-id="contentTypeId"
              :is-check-bot-inspection-disabled="isCheckBotInspectionDisabled"
              :is-no-metadata-upload-mode="isNoMetadataUploadMode"
              :mode="mode"
              :project-id="projectId"
              :reference-number="referenceNumber"
              :supplier-id="supplierId"
            />
          </PVTabPanel>
          <PVTabPanel :value="DocumentUploadViewTab.SINGLE_UPLOAD">
            <project-single-upload-form
              :content-type-fields="contentTypeFields"
              :content-type-id="contentTypeId"
              :check-bot-config-id="checkBotConfigId"
              :is-check-bot-inspection-disabled="isCheckBotInspectionDisabled"
              :mode="mode"
              :reference-number="referenceNumber"
              :project-id="projectId"
              :supplier-id="supplierId"
            />
          </PVTabPanel>
        </PVTabsPanels>
      </PVTabs>
    </loading-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { RouteLocationRaw, useRouter } from 'vue-router';
  import SingleUploadForm from '../../components/SingleUploadForm/SingleUploadForm.vue';
  import { ProjectType } from 'types';
  import { DocumentUploadViewTab } from './DocumentUploadView.types';
  import { Route } from '@/routes';
  import { routeParams } from '@/routes';
  import { useGetContentTypeFields } from '@/composables/api/content-types/use-get-content-type-fields';
  import { useTabs } from '@/composables/useTabs';
  import { stringToBoolean } from '@/helpers';

  const props = defineProps<{
    mode: ProjectType;
    checkBotConfigId?: string;
    contentTypeId: string;
    projectId: string;
    projectName: string;
    referenceNumber: string;
    supplierId?: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const { contentTypeFields } = useGetContentTypeFields(
    props.contentTypeId,
    true,
  );

  const isCheckBotInspectionDisabled = ref<boolean | undefined>(undefined);
  const isNoMetadataUploadMode = ref<boolean | undefined>(undefined);

  const { activeTab } = useTabs(
    Route.DOCUMENT_UPLOAD,
    DocumentUploadViewTab,
    DocumentUploadViewTab.MASS_UPLOAD,
  );
  function getTabLink(tab: DocumentUploadViewTab): RouteLocationRaw {
    const link = {
      name: Route.DOCUMENT_UPLOAD,
      query: {
        ...router.currentRoute.value.query,
        [routeParams[Route.DOCUMENT_UPLOAD].query.tab]: tab,
      },
    };
    return link;
  }

  function setIsCheckBotInspectionDisabled(): void {
    if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      isCheckBotInspectionDisabled.value = stringToBoolean(
        router.currentRoute.value.query.isCheckBotInspectionDisabled as string,
      );
    }
  }
  function setIsNoMetadataUploadMode(): void {
    if (props.mode === ProjectType.DIGITALIZATION_PROJECT) {
      isNoMetadataUploadMode.value = stringToBoolean(
        router.currentRoute.value.query.isNoMetadataUploadMode as string,
      );
    }
  }

  onMounted(() => {
    setIsCheckBotInspectionDisabled();
    setIsNoMetadataUploadMode();
  });
</script>

<style lang="scss" scoped>
  .document-upload-view {
    .title-panel {
      margin-bottom: 40px;
      h2 {
        font-weight: 500;
        font-size: 36px;
        line-height: 36px;
      }
      h1 {
        font-weight: 700;
        font-size: 50px;
      }
    }
  }
</style>
